import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";

export default function Register() {
    const { register } = useAuth();
    const nav = useNavigate();
    const [sp] = useSearchParams();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [phone, setPhone] = useState("");
    const [role, setRole] = useState(sp.get("role") === "owner" ? "owner" : "student");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const u = await register({ name, email, password, phone, role });
            toast.success(`Welcome, ${u.name}`);
            nav(u.role === "owner" ? "/owner" : "/centers");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Registration failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-[calc(100vh-4rem)] grid md:grid-cols-2">
            <div className="hidden md:block relative bg-[#0A0B10] text-white overflow-hidden">
                <img
                    src="https://images.unsplash.com/photo-1719159381981-1327b22aff9b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHw0fHxjb21wdXRlciUyMGxhYnxlbnwwfHx8fDE3ODUxODI2MzB8MA&ixlib=rb-4.1.0&q=85"
                    alt="Aspirants at a real test-center computer lab"
                    className="absolute inset-0 h-full w-full object-cover opacity-60 grayscale"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#0A0B10]/70 via-[#0A0B10]/50 to-[#0A0B10]/90" />
                <div className="absolute inset-0 bg-grid opacity-15" />
                <div className="relative h-full p-12 flex flex-col justify-between">
                    <div className="overline text-white/70">BookedSlot / Sign up</div>
                    <div>
                        <h1 className="font-heading text-4xl font-black tracking-tight leading-tight">Book seats. Not screens.</h1>
                        <p className="mt-4 text-white/70 max-w-md leading-relaxed">Join thousands of aspirants training under real exam conditions — real desks, real timers, real silence.</p>
                    </div>
                </div>
            </div>
            <div className="flex items-center justify-center p-8">
                <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="register-form">
                    <div>
                        <div className="overline mb-2">Create account</div>
                        <h2 className="font-heading text-3xl font-bold">Get started</h2>
                    </div>

                    <div className="space-y-2">
                        <Label>I am a…</Label>
                        <RadioGroup value={role} onValueChange={setRole} className="grid grid-cols-2 gap-2">
                            <label className={`border p-3 flex items-center gap-2 cursor-pointer rounded-sm ${role === "student" ? "border-primary bg-primary/5" : "border-border"}`}>
                                <RadioGroupItem value="student" data-testid="role-student" />
                                <span className="text-sm">Student</span>
                            </label>
                            <label className={`border p-3 flex items-center gap-2 cursor-pointer rounded-sm ${role === "owner" ? "border-primary bg-primary/5" : "border-border"}`}>
                                <RadioGroupItem value="owner" data-testid="role-owner" />
                                <span className="text-sm">Center Owner</span>
                            </label>
                        </RadioGroup>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="name">Full name</Label>
                        <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required className="rounded-sm" data-testid="reg-name" />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="rounded-sm" data-testid="reg-email" />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="phone">Phone</Label>
                        <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="rounded-sm" data-testid="reg-phone" />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="password">Password</Label>
                        <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="rounded-sm" data-testid="reg-password" />
                    </div>

                    <Button type="submit" disabled={loading} className="w-full rounded-sm bg-primary hover:bg-primary/90" data-testid="reg-submit">
                        {loading ? "Creating…" : "Create account"}
                    </Button>
                    <div className="text-sm text-muted-foreground text-center">
                        Already have an account? <Link to="/login" className="text-primary underline">Sign in</Link>
                    </div>
                </form>
            </div>
        </div>
    );
}
